import { IconButton } from "@circle-react-shared/uikit/HeaderV3/IconButton";
import { Dropdown } from "@circle-react-uikit/Dropdown";
import { useCurrentUser } from "@circle-react/contexts/punditUserContext";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useCircleV4 } from "@circle-react/hooks/useCircleV4";
import {
  IconButton as IconButtonCircleV4,
  type IconButtonProps as CompassIconButtonProps,
} from "@circleco/compass/components/IconButton";
import { noop } from "lodash";

import { t } from "@/i18n-js/instance";
import { TippyV2 } from "@/react/components/SettingsApp/CompassCompat/Tooltip";

import {
  getIconButtonProperties,
  getIconButtonPropertiesCircleV4,
} from "./helper";
import { useBookmarks } from "./hooks/useBookmarks";

interface BookmarkButtonProps {
  bookmarkType: "post" | "event" | "lesson" | "comment" | "message";
  className?: string;
  compassVariant?: CompassIconButtonProps["variant"];
  onSettled?: () => void;
  onSuccess?: (bookmarkId: number | null) => void;
  record: { id: number; [key: string]: any };
  tooltip?: string;
  variant?: "button" | "dropdown" | "minimal-post-modal";
}

export const BookmarkButton = ({
  bookmarkType,
  record,
  variant = "button",
  compassVariant = "outline",
  tooltip = record.bookmark_id
    ? t("bookmarks.actions.remove")
    : t(`bookmarks.actions.add_by_type.${bookmarkType}`),
  onSuccess,
  onSettled = noop,
  className = "",
}: BookmarkButtonProps) => {
  const currentUser = useCurrentUser();
  const { isCircleV4 } = useCircleV4();
  const bookmarkId = record.bookmark_id;
  const { createBookmark, deleteBookmark } = useBookmarks({
    bookmarkId,
    bookmarkType,
    record,
    onSuccess,
    onSettled,
    disableFetch: true,
  });

  const handleBookmarkToggle = () => {
    bookmarkId ? deleteBookmark() : createBookmark();
  };

  const allowedVariants = ["button", "dropdown", "minimal-post-modal"];
  const isUnsupportedVariantType = !allowedVariants.includes(variant);

  if (!currentUser || isUnsupportedVariantType) {
    return null;
  }

  if (variant === "button") {
    if (isCircleV4) {
      const { icon, size } = getIconButtonPropertiesCircleV4(
        bookmarkType,
        bookmarkId,
      );

      return (
        <TippyV2 content={tooltip} interactive={false}>
          <IconButtonCircleV4
            icon={icon}
            onClick={handleBookmarkToggle}
            size={size}
            className="group"
            variant={compassVariant}
            aria-label={t("header_v3.bookmarks")}
            data-testid="bookmark-button"
          />
        </TippyV2>
      );
    }

    const { iconName, iconSize, iconClassSize, buttonSize } =
      getIconButtonProperties(bookmarkType, bookmarkId);

    return (
      <TippyV2 content={tooltip} interactive={false}>
        <IconButton
          name={iconName}
          onClick={handleBookmarkToggle}
          iconSize={iconSize}
          buttonSize={buttonSize}
          className="group"
          variant="secondary"
          iconClassName={classNames(
            "text-dark",
            {
              "group-hover:text-darkest": !bookmarkId,
            },
            iconClassSize,
            className,
          )}
          ariaLabel={t("header_v3.bookmarks")}
          dataTestId="bookmark-button"
        />
      </TippyV2>
    );
  }

  if (variant === "dropdown") {
    return (
      <Dropdown.ItemLink onClick={handleBookmarkToggle}>
        {bookmarkId
          ? t("bookmarks.actions.remove")
          : t(`bookmarks.actions.add_by_type.${bookmarkType}`)}
      </Dropdown.ItemLink>
    );
  }

  if (variant === "minimal-post-modal") {
    return (
      <TippyV2 content={tooltip} interactive={false}>
        <IconButton
          name={bookmarkId ? "20-bookmark-fill" : "20-bookmark"}
          iconSize={20}
          iconClassName="!text-default group-hover:!text-dark"
          className="group"
          onClick={handleBookmarkToggle}
          buttonSize={32}
          ariaLabel={t("post.post_modal.bookmark_post")}
        />
      </TippyV2>
    );
  }

  return null;
};
